import { ts } from '@shared/lib/diagTs';
import { cqg } from './proto/cqgQuotes.js';
import { getSymbolByContractId } from './cqgQuoteDecoder';
import { livePositionsStore } from '@features/positions/lib/livePositionsStore';

/**
 * Browser-side CQG **PositionStatus** decoder — the live-open-positions twin of cqgQuoteDecoder.
 *
 * The relay (cqgRelay) forwards every raw frame to the BE (CqgInbound) AND decodes quotes locally.
 * This module additionally decodes the broker's AUTHORITATIVE current net positions in
 * `ServerMsg.position_statuses[]` (field 6) and pushes them into livePositionsStore, so the open-
 * positions list reflects exactly what CQG says is open — with no BE round-trip and, crucially, with
 * no fill-reconstruction (which produced phantom "double line" positions on refresh).
 *
 * SNAPSHOT vs DELTA (mirrors BE CqgPositionMerge.MergeInto exactly):
 *   - A frame carries the FULL lot list ONLY when `is_snapshot=true`. Update frames carry just the
 *     added/changed/deleted lots, keyed by the surrogate `OpenPosition.id` (qty 0 = lot deleted).
 *   - So frames MERGE into a per-(account, contract) LOT MAP; the net is computed over the MERGED
 *     map: signedNet = Σ (is_short ? −qty : qty); avgPrice = qty-weighted mean of price_correct over
 *     ABSOLUTE quantities. Netting a lone update frame reads its newest lot(s) as the whole position —
 *     the 2026-07-07 bug where a 12-lot MESU26 showed as 5 and later VANISHED (a delta netted to 0
 *     deleted the store key, and mergePositions hides BE rows missing from the live set).
 *   - Merged net 0 / empty lot map ⇒ FLAT — the (account, symbol) is genuinely closed.
 *
 * Symbol comes from the CURRENT-session contract resolution (getSymbolByContractId). Lots are merged
 * even while the symbol is unresolved (the map is keyed by contract id) — only the store APPLY is
 * deferred, so the state is complete the moment the symbol resolves. `resetPositionLots()` must be
 * called wherever `livePositionsStore.reset()` is (fresh socket): contract ids are session-scoped, and
 * CQG re-sends a full `is_snapshot=true` snapshot after every (re)subscribe. We only read live
 * ServerMsg.position_statuses; the BE owns persistence + the positions.cqg cross-check publish.
 */

/**
 * Convert a cqg.Decimal ({significand, exponent}) to a JS number: significand * 10^exponent.
 * significand is sint64 — even with the proto's `--force-number`, protobufjs can hand it back as a
 * string for large/var values, so we coerce with Number(...) to avoid string concatenation in the net
 * sum (a `0 + "2"` bug). NaN guards to 0.
 */
function decimalToNumber(d: { significand?: number | string | null; exponent?: number | null } | null | undefined): number {
  if (!d || d.significand == null) return 0;
  const significand = Number(d.significand);
  if (!Number.isFinite(significand)) return 0;
  const exp = d.exponent ?? 0;
  return exp === 0 ? significand : significand * Math.pow(10, exp);
}

/** One merged open-position lot (qty always positive; direction carried by isShort). */
interface Lot { qty: number; isShort: boolean; price: number }

// Per-(account, contract) lot maps for the CURRENT CQG session. Module-level (not React state): the
// decoder runs off the socket regardless of what's mounted. Keyed `${accountId}|${contractId}`.
const lotMaps = new Map<string, Map<number, Lot>>();

/** Drop all merged lot state — call alongside livePositionsStore.reset() on a fresh socket. */
export function resetPositionLots(): void {
  lotMaps.clear();
}

/** What a position-decode pass did — returned so the relay can surface it in the BE log for diagnosis. */
export interface PositionDecodeOutcome {
  /** `${account} ${symbol}` keys whose net was applied (set or cleared) this frame. */
  applied: string[];
  /** contractIds we saw a position for but couldn't resolve to a symbol yet (deferred). */
  deferredContractIds: number[];
}

/**
 * Decode one ServerMsg's PositionStatuses, merge each into its (account, contract) lot map, and apply
 * the MERGED net to the live-positions store. Never throws — a malformed frame is skipped (the relay
 * keeps forwarding raw bytes to the BE regardless).
 */
export function decodeAndApplyPositions(bytes: Uint8Array): PositionDecodeOutcome {
  const outcome: PositionDecodeOutcome = { applied: [], deferredContractIds: [] };
  let msg: ReturnType<typeof cqg.ServerMsg.decode>;
  try {
    msg = cqg.ServerMsg.decode(bytes);
  } catch {
    return outcome;
  }

  const statuses = msg.positionStatuses;
  if (!statuses || !statuses.length) return outcome;

  for (const ps of statuses) {
    const contractId = ps.contractId;
    if (contractId == null) continue;

    const account = String(ps.accountId ?? 0);

    // 1) MERGE the frame into the lot map FIRST (symbol-independent — keyed by contract id), so the
    //    state is complete even while the symbol is still unresolved.
    const mapKey = `${account}|${contractId}`;
    let lots = lotMaps.get(mapKey);
    if (!lots) { lots = new Map<number, Lot>(); lotMaps.set(mapKey, lots); }
    if (ps.isSnapshot) lots.clear(); // snapshot = the FULL current lot list

    for (const op of ps.openPositions ?? []) {
      const lotId = op.id ?? 0;
      const qty = decimalToNumber(op.qty);
      if (qty === 0) lots.delete(lotId); // qty 0 = the lot is deleted
      else lots.set(lotId, { qty, isShort: !!op.isShort, price: op.priceCorrect ?? 0 });
    }

    // 2) Symbol resolution: current-session only. Defer (skip the store APPLY) when unresolved — the
    //    lot map above already merged, so nothing is lost; CQG re-sends and /positions backstops.
    const symbol = getSymbolByContractId(contractId);
    if (!symbol) {
      if (!outcome.deferredContractIds.includes(contractId)) outcome.deferredContractIds.push(contractId);
      continue;
    }

    // 3) NET the MERGED lot map (mirrors BE CqgPositionMerge.NetOfLots): signed sum (short = negative);
    //    avg price = qty-weighted mean over the absolute quantities.
    let signedNet = 0;
    let weightedPriceSum = 0;
    let absQtySum = 0;
    for (const lot of lots.values()) {
      signedNet += lot.isShort ? -lot.qty : lot.qty;
      weightedPriceSum += lot.price * lot.qty;
      absQtySum += lot.qty;
    }
    const avgPrice = absQtySum > 0 ? weightedPriceSum / absQtySum : 0;
    if (signedNet === 0) lotMaps.delete(mapKey); // truly flat — drop the map entry too

    const changed = livePositionsStore.setFromPositionStatus(account, symbol, signedNet, avgPrice);
    const key = `${account} ${symbol}`;
    if (changed) outcome.applied.push(key);

    // English flow log (localhost only): what CQG says this (account, symbol) currently is.
    if (import.meta.env.DEV) {
      if (signedNet === 0) {
        console.log('%s [live-position] 📊 CQG says %s net=0 - FLAT (closed) (acct %s)', ts(), symbol, account);
      } else {
        const signed = signedNet > 0 ? `+${signedNet}` : String(signedNet);
        const dir = signedNet > 0 ? 'LONG' : 'SHORT';
        console.log(
          '%s [live-position] 📊 CQG says %s net=%s %s @ %s (acct %s)',
          ts(), symbol, signed, dir, avgPrice, account,
        );
      }
    }
  }

  return outcome;
}
